// Sentry error tracking (browser) — ecommerce-admin.
//
// DSN is supplied at runtime via window.SENTRY_DSN (set in public/config.js, which is the
// tenant config this admin deployment loads). A Sentry DSN is a public, write-only ingest
// key, so it is safe in client config. No DSN => no init.
//
// @sentry/vue is pinned to the v7 line to stay compatible with this app's older
// vue-cli 4 / webpack 4 toolchain (v7 supports Vue 3). See package.json.
import * as Sentry from '@sentry/vue'

// Admin login uses magic-link/reset tokens carried in URL query/hash (?magiclink=…, ?token=…).
// Sentry auto-records navigation + fetch URLs as breadcrumbs, so redact token-ish params
// before any event leaves the browser — otherwise a captured error could exfiltrate a live token.
const SENSITIVE_PARAM_RE =
  /([?&#](?:magiclink|token|reset|code|key|apikey|api_key|password|pwd|secret)=)[^&#\s]*/gi

export function scrubUrl(value) {
  if (typeof value !== 'string' || value === '') return value
  return value.replace(SENSITIVE_PARAM_RE, '$1[REDACTED]')
}

// Initialize Sentry from runtime window config. Call BEFORE app.mount so mount-time
// errors are captured. Passing the Vue 3 `app` lets @sentry/vue hook app.config.errorHandler.
export function initSentry(app) {
  const dsn = window.SENTRY_DSN
  if (!dsn) return false

  try {
    Sentry.init({
      app,
      dsn,
      environment: window.SENTRY_ENVIRONMENT || 'production',
      // Error capture only. BrowserTracing omitted: it would turn the page URL into a
      // transaction name, re-introducing the token leak the scrubbers below prevent.
      integrations: [],
      tracesSampleRate: 0,
      sendDefaultPii: false,
      beforeBreadcrumb(breadcrumb) {
        if (breadcrumb && breadcrumb.data) {
          const d = breadcrumb.data
          if (d.url) d.url = scrubUrl(d.url)
          if (d.to) d.to = scrubUrl(d.to)
          if (d.from) d.from = scrubUrl(d.from)
        }
        if (breadcrumb && typeof breadcrumb.message === 'string') {
          breadcrumb.message = scrubUrl(breadcrumb.message)
        }
        return breadcrumb
      },
      beforeSend(event) {
        if (event.request) {
          if (event.request.url) event.request.url = scrubUrl(event.request.url)
          if (event.request.query_string) {
            event.request.query_string = scrubUrl(String(event.request.query_string))
          }
        }
        return event
      },
    })
    window.Sentry = Sentry
    return true
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[sentry] init skipped:', e && e.message)
    return false
  }
}
